import { useStatutes } from '../hooks/useStatutes';
import { Loader2, ArrowLeft, Link as LinkIcon, Check, Menu, Search } from 'lucide-react';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import UploadStatuteModal from '../components/UploadStatuteModal';
import { StatuteAntiScrapeBoundary } from '../components/StatuteAntiScrapeBoundary';
import { writeStatuteClipboard } from '../components/StatutePreviewReader';
import type { StatutePreviewUiSection } from '../components/StatutePreviewReader';
import { LexDraftStatuteReader } from '../components/library/LexDraftStatuteReader';
import { AsyncContentReveal } from '../components/motion/AsyncContentReveal';
import { FirestoreLoadingSpinner } from '../components/motion/FirestoreLoadingSpinner';
import { RevealOnMount } from '../components/motion/RevealOnMount';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { storage } from '../lib/firebase';
import { getBytes, ref as storageRef } from 'firebase/storage';
import { normalizePreviewSections } from '../lib/statuteSections';
import { parseStatuteTextForReader } from '../lib/statuteFormat';
import { adaptStatuteAny } from '../lib/statute/detect';
import { toPreviewSections } from '../lib/statute/toPreviewSections';
import { api } from '../lib/apiClient';
import { appUrl } from '../lib/domains';
import StatuteSeoFaqPanel from '../components/library/StatuteSeoFaqPanel';

type QuickSearchHit = {
  statuteId: string;
  sectionId: string | null;
  label: string;
  title: string | null;
  snippet: string;
};

function deriveStoragePathFromFileUrl(url?: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const marker = '/o/';
    const idx = parsed.pathname.indexOf(marker);
    if (idx === -1) return null;
    const encodedPath = parsed.pathname.slice(idx + marker.length);
    const decoded = decodeURIComponent(encodedPath || '');
    return decoded || null;
  } catch {
    return null;
  }
}

type LibraryDetailProps = {
  isPublic?: boolean;
  basePath?: string;
};

export default function LibraryDetail({ isPublic = false, basePath = '/library' }: LibraryDetailProps) {
  const { statutes, loading, error } = useStatutes();
  const { id: routeId } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [remoteContent, setRemoteContent] = useState<string | null>(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const [copyPageOk, setCopyPageOk] = useState(false);
  const [sectionsPanelOpen, setSectionsPanelOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [quickQuery, setQuickQuery] = useState('');
  const [quickHits, setQuickHits] = useState<QuickSearchHit[]>([]);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const quickSearchRef = useRef<HTMLDivElement | null>(null);

  const selectedStatuteId = routeId || null;
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  useEffect(() => {
    if (!isPublic) return;
    if (!user || !selectedStatuteId) return;
    navigate(`/library/${selectedStatuteId}`, { replace: true });
  }, [isPublic, user, selectedStatuteId, navigate]);

  useEffect(() => {
    setSelectedSectionId(null);
  }, [selectedStatuteId]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setSectionsPanelOpen(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const handleReaderSectionIdChange = useCallback((id: string) => {
    setSelectedSectionId(String(id));
  }, []);

  const openSectionFromSearch = useCallback(
    (sectionId: string) => {
      if (!selectedStatuteId) return;
      const normalized = String(sectionId).replace(/^section-/i, '');
      setSelectedSectionId(String(sectionId));
      navigate(`${basePath}/${selectedStatuteId}#section-${normalized}`, { replace: true });
      setQuickOpen(false);
    },
    [navigate, selectedStatuteId],
  );

  const selectedStatute = useMemo(() => statutes.find((s) => s.id === selectedStatuteId), [statutes, selectedStatuteId]);

  useEffect(() => {
    let cancelled = false;
    const loadRemoteContent = async () => {
      setRemoteContent(null);
      setRemoteError(null);
      if (!selectedStatute) return;
      const inline = typeof selectedStatute.content === 'string' ? selectedStatute.content.trim() : '';
      if (inline) return;
      setRemoteLoading(true);
      try {
        const pathFromUrl = deriveStoragePathFromFileUrl(
          selectedStatute.fileUrl || selectedStatute.documentUrl || selectedStatute.pdfUrl,
        );
        const path = selectedStatute.storagePath || pathFromUrl;
        if (!path) throw new Error('No storage path found for this statute.');
        const bytes = await getBytes(storageRef(storage, path));
        const text = new TextDecoder().decode(bytes);
        if (!cancelled) setRemoteContent(text);
      } catch (err) {
        console.error('Failed to load remote statute content:', err);
        if (!cancelled) setRemoteError('Failed to load statute content from storage.');
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    };
    loadRemoteContent();
    return () => {
      cancelled = true;
    };
  }, [selectedStatute]);

  const parsedSections = useMemo(() => {
    if (!selectedStatute) return [];
    const contentText =
      typeof selectedStatute.content === 'string' && selectedStatute.content.trim()
        ? selectedStatute.content
        : remoteContent || '';
    if (!contentText) return [];

    // Canonical pipeline: JSON.parse -> detect format -> adapter -> bridge.
    // Falls back to the legacy text-based parser for non-JSON content,
    // unrecognized formats, or when the canonical pipeline yields nothing.
    try {
      const json = JSON.parse(contentText);
      const statute = adaptStatuteAny(json, selectedStatute.category);
      const bridged = toPreviewSections(statute);
      if (bridged.length) return bridged as StatutePreviewUiSection[];
    } catch {
      // not JSON or no canonical match — fall through to legacy parser
    }

    const base = parseStatuteTextForReader(contentText);
    if (base.length) {
      return normalizePreviewSections(base as Record<string, unknown>[]).map((section, i) => ({
        ...section,
        tocListIndex: i + 1,
      })) as StatutePreviewUiSection[];
    }
    return [
      {
        id: 'full-text',
        label: 'DOCUMENT',
        title: selectedStatute.title,
        content: contentText,
        tocListIndex: 1,
      },
    ] as StatutePreviewUiSection[];
  }, [selectedStatute, remoteContent]);

  const libraryUnitLabel = useMemo((): 'Section' | 'Article' => {
    if (!selectedStatute) return 'Section';
    if (selectedStatute.readerUnitLabel === 'Article') return 'Article';
    if (selectedStatute.readerUnitLabel === 'Section') return 'Section';
    return parsedSections.some((s) => /^art-/i.test(String(s.id))) ? 'Article' : 'Section';
  }, [selectedStatute, parsedSections]);

  const libraryTocVariant = useMemo((): 'default' | 'qso' => {
    if (!selectedStatute) return 'default';
    return selectedStatute.readerTocVariant === 'qso' ? 'qso' : 'default';
  }, [selectedStatute]);

  const hasInlineStatuteContent = Boolean(
    selectedStatute && typeof selectedStatute.content === 'string' && selectedStatute.content.trim(),
  );
  const readerLoading = Boolean(selectedStatute && !hasInlineStatuteContent && remoteLoading);
  const readerError = selectedStatute && !hasInlineStatuteContent ? remoteError : null;

  const unknownStatute = Boolean(selectedStatuteId && !loading && !selectedStatute);
  const needsRemoteBody = Boolean(selectedStatute && !hasInlineStatuteContent);
  /** Terminal reader state for Playwright prerender (never spin forever on empty/error edge cases). */
  const statutePrerenderReady = Boolean(
    unknownStatute ||
      (selectedStatute &&
        (!needsRemoteBody
          ? parsedSections.length > 0 || Boolean(readerError)
          : !readerLoading &&
            (parsedSections.length > 0 ||
              Boolean(readerError) ||
              remoteContent !== null))),
  );

  const copyStatuteLink = async () => {
    const ok = await writeStatuteClipboard(window.location.href);
    if (ok) {
      setCopyPageOk(true);
      window.setTimeout(() => setCopyPageOk(false), 1600);
    }
  };

  const runQuickSearch = useCallback(
    async (query: string) => {
      if (!api.isConfigured() || !selectedStatuteId || query.trim().length < 2) {
        setQuickHits([]);
        setQuickOpen(false);
        return;
      }
      setQuickLoading(true);
      try {
        const send = isPublic ? api.postPublic : api.post;
        const path = isPublic
          ? '/api/library/public/keyword-search'
          : '/api/library/keyword-search';
        const res = await send<{ hits: QuickSearchHit[] }>(path, {
          query,
          topK: 10,
          statuteId: selectedStatuteId,
          corpus_filter: ['operative'],
        });
        const hits = res.hits || [];
        setQuickHits(hits);
        setQuickOpen(hits.length > 0);
      } catch {
        setQuickHits([]);
        setQuickOpen(false);
      } finally {
        setQuickLoading(false);
      }
    },
    [selectedStatuteId, isPublic],
  );

  useEffect(() => {
    if (quickQuery.trim().length < 2) {
      setQuickHits([]);
      setQuickOpen(false);
      setQuickLoading(false);
      return;
    }
    const timer = window.setTimeout(() => {
      void runQuickSearch(quickQuery);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [quickQuery, runQuickSearch]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target || !quickSearchRef.current) return;
      if (!quickSearchRef.current.contains(target)) {
        setQuickOpen(false);
      }
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setQuickOpen(false);
    };
    window.addEventListener('mousedown', onClickOutside);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('keydown', onEsc);
    };
  }, []);

  if (!loading && !selectedStatuteId) {
    return (
      <StatuteAntiScrapeBoundary>
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <p className="text-[#64748B] mb-4">No statute selected.</p>
          <button type="button" onClick={() => navigate(basePath)} className="text-primary font-bold text-sm">
            Back to Library
          </button>
        </div>
      </StatuteAntiScrapeBoundary>
    );
  }

  const pdfUrl =
    selectedStatute?.fileUrl || selectedStatute?.documentUrl || selectedStatute?.pdfUrl || '';
  const looksPdf = /\.pdf(\?|$)/i.test(pdfUrl);

  return (
    <StatuteAntiScrapeBoundary>
      {/* App-shell: takes over the Outlet wrapper (which is overflow-hidden for
          /library/:id in Layout.tsx) so only the document area inside scrolls. */}
      <div
        className="absolute inset-x-0 top-0 bottom-0 w-full h-full bg-[#F5F5F7] flex flex-col z-20"
        data-statute-loaded={statutePrerenderReady ? 'true' : 'false'}
      >
        <RevealOnMount index={0}>
        {/* Header — pinned because it lives outside any scroll container */}
        <div className="bg-white/40 backdrop-blur-2xl border-b border-white/50 px-3 sm:px-4 py-2.5 sm:py-3 flex flex-wrap items-center gap-2 min-h-[52px] sm:min-h-[56px] shrink-0 shadow-sm z-40">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 lg:flex-none">
            <button
              type="button"
              onClick={() => navigate(basePath)}
              className="p-2 rounded-lg text-gray-600 transition-colors shrink-0 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              title="Back to Library"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setSectionsPanelOpen(true)}
              className="lg:hidden p-2 rounded-lg text-gray-600 shrink-0 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              aria-label="Open sections"
              title="Sections"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 truncate tracking-tight min-w-0">
              {loading ? 'Loading…' : selectedStatute?.title || 'Statute'}
            </h1>
          </div>
          <div className="order-3 w-full lg:order-2 lg:flex-1 lg:flex lg:justify-center lg:px-6">
            <div ref={quickSearchRef} className="relative w-full lg:max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={quickQuery}
              onChange={(e) => {
                setQuickQuery(e.target.value);
                if (e.target.value.trim().length >= 2) setQuickOpen(true);
              }}
              onFocus={() => {
                if (quickQuery.trim().length >= 2 && quickHits.length > 0) setQuickOpen(true);
              }}
              placeholder="Search this law..."
              className="w-full min-w-0 pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-primary outline-none"
            />
            {quickLoading ? <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" /> : null}
            {quickOpen && quickQuery.trim().length >= 2 ? (
              <div className="absolute top-[calc(100%+8px)] left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border border-slate-200/80 shadow-xl rounded-xl overflow-hidden">
                <div className="max-h-80 overflow-y-auto p-1.5 space-y-1">
                  {quickHits.length === 0 && !quickLoading ? (
                    <p className="text-xs text-slate-500 px-2 py-3 text-center">No keyword matches found.</p>
                  ) : (
                    quickHits.map((hit, idx) => (
                      <button
                        type="button"
                        key={`${hit.sectionId}-${idx}`}
                        onClick={() => {
                          if (hit.sectionId != null) openSectionFromSearch(String(hit.sectionId));
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg border border-transparent hover:border-primary/30 hover:bg-slate-50 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      >
                        <div className="text-[10px] font-bold uppercase text-primary">{hit.label}</div>
                        {hit.title ? <div className="text-xs font-semibold text-slate-900 line-clamp-1">{hit.title}</div> : null}
                        <div className="text-[11px] text-slate-600 line-clamp-2 mt-0.5">{hit.snippet}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : null}
            </div>
          </div>
          <div className="order-2 lg:order-3 flex items-center gap-1.5 sm:gap-2 shrink-0 flex-wrap justify-end ml-auto">
            <button
              type="button"
              onClick={copyStatuteLink}
              className="group bg-white backdrop-blur border border-slate-200/80 text-slate-800 px-2.5 py-1.5 sm:px-3 md:px-4 rounded-full text-xs sm:text-sm font-semibold hover:shadow-md transition-all shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              {copyPageOk ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <LinkIcon className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{copyPageOk ? 'Copied' : 'Copy link'}</span>
            </button>
            {looksPdf ? (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white backdrop-blur border border-slate-200/80 text-slate-800 px-2.5 py-1.5 sm:px-3 md:px-4 rounded-full text-xs sm:text-sm font-semibold hover:shadow-md transition-all shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                <span>Download</span>
                <span className="bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors px-1.5 py-[3px] rounded-full text-[9px] uppercase tracking-widest font-black leading-none border border-slate-200/60">
                  PDF
                </span>
              </a>
            ) : null}
            {!isPublic && isAdmin ? (
              <button
                type="button"
                onClick={() => setIsUploadOpen(true)}
                className="hidden sm:inline-flex text-xs font-bold text-primary px-3 py-1.5 rounded-lg border border-primary/30 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                Add Statute
              </button>
            ) : null}
          </div>
        </div>
        </RevealOnMount>

        {/* Body — non-scrolling positioned context for the floating sections
            panel and the inner document scroll area. */}
        <AsyncContentReveal loading={loading} minHeight="min-h-0" className="flex flex-1 w-full min-h-0" skeleton={<FirestoreLoadingSpinner />}>
        <div className="flex flex-1 w-full bg-[#F5F5F7] overflow-hidden relative min-h-0">
          {selectedStatute && selectedStatuteId ? (
            <LexDraftStatuteReader
              sections={parsedSections}
              loading={readerLoading}
              error={readerError}
              unitLabel={libraryUnitLabel}
              tocVariant={libraryTocVariant}
              linkBasePath={`${basePath}/${selectedStatuteId}`}
              locationHash={location.hash}
              selectedSectionId={selectedSectionId}
              onSelectedSectionIdChange={handleReaderSectionIdChange}
              sectionsPanelOpen={sectionsPanelOpen}
              onSectionsPanelOpenChange={setSectionsPanelOpen}
              syncUrlForSection={(sectionId) => {
                const normalized = String(sectionId).replace(/^section-/i, '');
                navigate(`${basePath}/${selectedStatuteId}#section-${normalized}`, { replace: true });
              }}
              statuteId={selectedStatuteId}
              readerTopSlot={
                isPublic && selectedStatute ? (
                  <StatuteSeoFaqPanel statute={selectedStatute} />
                ) : null
              }
              isPublic={isPublic}
              onPublicLogin={() => { window.location.href = appUrl('/login'); }}
              onPublicSignup={() => { window.location.href = appUrl('/login'); }}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <p className="text-[#64748B] max-w-md mb-4">
                {error ||
                  'This statute may have been removed, or the link is invalid. Return to the library to pick another act.'}
              </p>
              <button type="button" onClick={() => navigate(basePath)} className="text-primary font-bold text-sm">
                Back to Library
              </button>
            </div>
          )}
        </div>
        </AsyncContentReveal>
      </div>

      <UploadStatuteModal isOpen={isUploadOpen} onClose={() => setIsUploadOpen(false)} />
    </StatuteAntiScrapeBoundary>
  );
}
